'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
  ReactElement,
} from 'react';

import { ROLE_IDS } from '@/constants/roles';
import { apiGet } from '@/lib/api/client';
import { STORAGE_KEYS, getStoredNumber, setStoredNumber } from '@/lib/utils/storage';

interface MeResponse {
  ID: number;
  FullName: string;
  Email: string;
  RoleID: number;
  OrganizationID: number | null;
  IsDemo?: boolean;
  NeedsTermsReacceptance?: boolean;
}

interface PermissionContextType {
  roleId: number | null;
  setRoleId: React.Dispatch<React.SetStateAction<number | null>>;
  isAdmin: boolean;
  isMember: boolean;
  /** 所属組織がデモ環境かどうか(デモ中バナー表示用) */
  isDemo: boolean;
  isLoading: boolean;
  /** 現行の規約バージョンに未同意かどうか(再同意モーダル表示用) */
  needsTermsReacceptance: boolean;
  /** サーバ(/auth/me)からロールを再取得する。ログイン/サインアップ直後のSPA遷移で呼ぶ */
  refreshRole: () => Promise<void>;
}

const defaultContextValue: PermissionContextType = {
  roleId: null,
  setRoleId: () => {
    console.warn('setRoleId called before initialization');
  },
  isAdmin: false,
  isMember: false,
  isDemo: false,
  isLoading: true,
  needsTermsReacceptance: false,
  refreshRole: async () => {
    console.warn('refreshRole called before initialization');
  },
};

const PermissionContext = createContext<PermissionContextType>(defaultContextValue);

export const PermissionProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [roleId, setRoleId] = useState<number | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [needsTermsReacceptance, setNeedsTermsReacceptance] = useState(false);

  // サーバ(/auth/me)からロールを取得する。
  // tokenはHttpOnly CookieのためJSから存在確認できない。常に問い合わせ、
  // 未認証(401)の場合は静かに終了する(skipAuthRedirectでリダイレクトも抑止)
  const refreshRole = useCallback(async () => {
    const result = await apiGet<MeResponse>('/auth/me', { skipAuthRedirect: true });
    if (result.data?.RoleID) {
      setRoleId(result.data.RoleID);
    }
    setIsDemo(result.data?.IsDemo ?? false);
    // 未認証(data無し)では再同意フラグは立てない
    setNeedsTermsReacceptance(result.data?.NeedsTermsReacceptance ?? false);
    setIsLoading(false);
  }, []);

  // クライアントマウント後に初期化:
  // 1. localStorageの値で即座に初期表示(ちらつき防止のキャッシュ)
  // 2. 認証済みならサーバから正値を取得して上書き
  //    (サインアップ直後などlocalStorage未保存でもロールが解決される)
  useEffect(() => {
    const savedRoleId = getStoredNumber(STORAGE_KEYS.ROLE_ID);
    if (savedRoleId !== null) {
      setRoleId(savedRoleId);
    }
    refreshRole();
  }, [refreshRole]);

  // 変更があったら保存
  useEffect(() => {
    if (roleId !== null) {
      setStoredNumber(STORAGE_KEYS.ROLE_ID, roleId);
    }
  }, [roleId]);

  // 権限判定
  const isAdmin = roleId === ROLE_IDS.ADMIN;
  const isMember = roleId === ROLE_IDS.MEMBER;

  return (
    <PermissionContext.Provider
      value={{
        roleId,
        setRoleId,
        isAdmin,
        isMember,
        isDemo,
        isLoading,
        needsTermsReacceptance,
        refreshRole,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = (): PermissionContextType => useContext(PermissionContext);
